const chapter = {
  id: 'solution-of-triangles',
  slug: 'solution-of-triangles',
  subject: 'mathematics',
  subjectLabel: 'Mathematics',
  exam: 'JEE',
  title: {
    english: 'Solution of Triangles & Heights and Distances',
        hindi: 'त्रिभुजों का हल और ऊँचाई-दूरी',
  },
  estReadMinutes: 40,
  difficulty: {
    english: 'Medium',
        hindi: 'मध्यम',
  },
  weightage: {
    english: '1-2 questions in JEE Main',
        hindi: 'जेईई मेन में 1-2 प्रश्न',
  },
  syllabusTags: ['Trigonometry', 'JEE Main', 'JEE Advanced'],
  sections: [
    {
      id: 'sine-cosine-rules',
      title: {
        english: 'Sine, Cosine & Projection Rules',
        hindi: 'ज्या, कोज्या और प्रक्षेप नियम',
      },
      blocks: [
        {
          type: 'paragraph',
          body: {
            english: 'In triangle $ABC$, sides $a, b, c$ are opposite to vertices $A, B, C$. Let $R$ be circumradius, $r$ inradius, $s = (a+b+c)/2$ semi-perimeter, $\\Delta$ area.',
        hindi: 'त्रिभुज $ABC$ में, भुजाएँ $a, b, c$ शीर्षों $A, B, C$ के विपरीत हैं। मान लीजिए $R$ परिधि, $r$ अंतःत्रिज्या, $s = (a+b+c)/2$ अर्ध-परिधि, $\\Delta$ क्षेत्र है।',
          },
        },
        {
          type: 'equation',
          equation: '\\dfrac{a}{\\sin A} = \\dfrac{b}{\\sin B} = \\dfrac{c}{\\sin C} = 2R \\quad \\text{(Sine rule)}',
        },
        {
          type: 'equation',
          equation: '\\cos A = \\dfrac{b^2 + c^2 - a^2}{2bc} \\quad \\text{(Cosine rule)}',
        },
        {
          type: 'equation',
          equation: 'a = b \\cos C + c \\cos B \\quad \\text{(Projection rule)}',
        },
      ],
    },
    {
      id: 'half-angle-area',
      title: {
        english: 'Half-Angle Formulas, Area & Radii',
        hindi: 'अर्धकोण सूत्र, क्षेत्रफल और त्रिज्याएँ',
      },
      blocks: [
        {
          type: 'equation',
          equation: '\\sin \\dfrac{A}{2} = \\sqrt{\\dfrac{(s-b)(s-c)}{bc}}, \\quad \\cos \\dfrac{A}{2} = \\sqrt{\\dfrac{s(s-a)}{bc}}, \\quad \\tan \\dfrac{A}{2} = \\sqrt{\\dfrac{(s-b)(s-c)}{s(s-a)}}',
        },
        {
          type: 'equation',
          equation: '\\Delta = \\tfrac{1}{2} bc \\sin A = \\sqrt{s(s-a)(s-b)(s-c)} \\quad \\text{(Heron)}',
        },
        {
          type: 'equation',
          equation: 'R = \\dfrac{abc}{4\\Delta}, \\quad r = \\dfrac{\\Delta}{s}, \\quad r_1 = \\dfrac{\\Delta}{s-a}, \\quad r_1 r_2 r_3 = r s^2',
        },
        {
          type: 'note',
          body: {
            english: '$r + r_1 + r_2 + r_3 = 4R$. Also $r_1 + r_2 + r_3 - r = 4R$.',
        hindi: '$r + r_1 + r_2 + r_3 = 4R$. साथ ही $r_1 + r_2 + r_3 - r = 4R$.',
          },
        },
      ],
    },
    {
      id: 'napier',
      title: {
        english: 'Napier\'s Analogy & m-n Theorem',
        hindi: 'नेपियर की सादृश्यता और एम-एन प्रमेय',
      },
      blocks: [
        {
          type: 'equation',
          equation: '\\tan \\dfrac{B-C}{2} = \\dfrac{b-c}{b+c} \\cot \\dfrac{A}{2}',
        },
        {
          type: 'paragraph',
          body: {
            english: '**m-n theorem**: If point $D$ on $BC$ divides it as $BD:DC = m:n$ and $\\angle BAD = \\alpha$, $\\angle DAC = \\beta$, $\\angle ADC = \\theta$, then $(m+n)\\cot\\theta = m\\cot\\alpha - n\\cot\\beta$ and $(m+n)\\cot\\theta = n\\cot B - m\\cot C$.',
        hindi: '**एम-एन प्रमेय**: यदि $BC$ पर बिंदु $D$ इसे $BD:DC = m:n$ और $\\angle BAD = \\alpha$, $\\angle DAC = \\beta$, $\\angle ADC = \\theta$ के रूप में विभाजित करता है, तो $(m+n)\\cot\\theta = m\\cot\\alpha - n\\cot\\beta$ और $(m+n)\\cot\\theta = n\\cot B - m\\cot C$।',
          },
        },
      ],
    },
    {
      id: 'heights-distances',
      title: {
        english: 'Heights & Distances — Working Tips',
        hindi: 'ऊँचाई और दूरी — कार्यशील युक्तियाँ',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Angle of elevation** — line of sight above horizontal.',
        hindi: '**उन्नयन कोण** — क्षैतिज से ऊपर दृष्टि रेखा।',
            },
            {
              english: '**Angle of depression** — line of sight below horizontal.',
        hindi: '**अवनमन कोण** - क्षैतिज के नीचे दृष्टि रेखा।',
            },
            {
              english: 'Always draw a clear figure with horizontal/vertical reference; mark all known angles & distances.',
        hindi: 'हमेशा horizontal/vertical संदर्भ के साथ एक स्पष्ट आकृति बनाएं; सभी ज्ञात कोणों और दूरियों को चिह्नित करें।',
            },
            {
              english: 'Use right triangles ($\\tan$ first), then sine/cosine rule for oblique cases.',
        hindi: 'तिरछे मामलों के लिए समकोण त्रिभुज ($\\tan$ पहले) का उपयोग करें, फिर sine/cosine नियम का उपयोग करें।',
            },
            {
              english: 'For two observers / two flagstaff problems, set up two equations in two unknowns.',
        hindi: 'दो पर्यवेक्षकों/दो फ़्लैगस्टाफ़ समस्याओं के लिए, दो अज्ञात में दो समीकरण स्थापित करें।',
            },
          ],
        },
        {
          type: 'example',
          problem: {
            english: 'A tower of height $h$ subtends angles $\\alpha$ and $\\beta$ at two points on the ground at distances $a$ and $b$ from the base, on the same line. Find $h$.',
        hindi: 'ऊंचाई $h$ का एक टावर एक ही रेखा पर, आधार से दूरी $a$ और $b$ पर जमीन पर दो बिंदुओं पर कोण $\\alpha$ और $\\beta$ घटाता है। $h$ खोजें।',
          },
          solution: {
            english: '$h = a \\tan\\alpha = b \\tan\\beta$ → consistent. If only $a - b = d$ is known: $h = \\dfrac{d}{\\cot\\beta - \\cot\\alpha}$.',
        hindi: '$h = a \\tan\\alpha = b \\tan\\beta$ → सुसंगत। यदि केवल $a - b = d$ ज्ञात है: $h = \\dfrac{d}{\\cot\\beta - \\cot\\alpha}$।',
          },
        },
        {
          type: 'example',
          problem: {
            english: 'Angle of elevation of top of tower from a point is $30°$. Walking $40$ m towards the tower, it becomes $60°$. Find tower height.',
        hindi: 'एक बिंदु से टावर के शीर्ष का उन्नयन कोण $30°$ है। टावर की ओर $40$ मीटर चलने पर, यह $60°$ हो जाता है। टावर की ऊंचाई ज्ञात करें.',
          },
          solution: {
            english: 'Let height $h$, initial distance $x$. $\\tan 30° = h/x$, $\\tan 60° = h/(x-40)$. From these $x = h\\sqrt 3$ and $x - 40 = h/\\sqrt 3$ → $h\\sqrt 3 - h/\\sqrt 3 = 40 \\Rightarrow h \\cdot \\frac{2}{\\sqrt 3} = 40 \\Rightarrow h = 20\\sqrt 3$ m.',
        hindi: 'माना ऊँचाई $h$, प्रारंभिक दूरी $x$ है। $\\tan 30° = h/x$, $\\tan 60° = h/(x-40)$. इनसे $x = h\\sqrt 3$ और $x - 40 = h/\\sqrt 3$ → $h\\sqrt 3 - h/\\sqrt 3 = 40 \\Rightarrow h \\cdot \\frac{2}{\\sqrt 3} = 40 \\Rightarrow h = 20\\sqrt 3$ मी.',
          },
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: 'a/\\sin A = 2R',
      desc: {
        english: 'Sine rule',
        hindi: 'साइन नियम',
      },
    },
    {
      latex: '\\cos A = (b^2 + c^2 - a^2) / (2bc)',
      desc: {
        english: 'Cosine rule',
        hindi: 'कोसाइन नियम',
      },
    },
    {
      latex: '\\Delta = \\sqrt{s(s-a)(s-b)(s-c)}',
      desc: {
        english: 'Heron',
        hindi: 'बगुला',
      },
    },
    {
      latex: 'R = abc/(4\\Delta)',
      desc: {
        english: 'Circumradius',
        hindi: 'सर्कमरेडियस',
      },
    },
    {
      latex: 'r = \\Delta/s',
      desc: {
        english: 'Inradius',
        hindi: 'इनरेडियस',
      },
    },
    {
      latex: 'r + r_1 + r_2 + r_3 = 4R',
      desc: {
        english: '',
        hindi: 'आर + आर 1 + आर 2 + आर 3 = 4आर',
      },
    },
    {
      latex: '\\tan\\tfrac{A}{2} = \\sqrt{(s-b)(s-c)/(s(s-a))}',
      desc: {
        english: '',
        hindi: '2=',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'JEE Main',
      tag: 'Sine rule',
      problem: {
        english: 'In $\\triangle ABC$, $a = 5, b = 7, \\sin A = 3/4$. Find $\\sin B$.',
        hindi: '$\\triangle ABC$, $a = 5, b = 7, \\sin A = 3/4$ में। $\\sin B$ खोजें।',
      },
      answer: {
        english: '$21/20$ — impossible, so no such triangle.',
        hindi: '$21/20$ - असंभव, इसलिए ऐसा कोई त्रिकोण नहीं है।',
      },
      solution: {
        english: '$\\sin B = (b/a)\\sin A = (7/5)(3/4) = 21/20 > 1$ ⇒ invalid.',
        hindi: '$\\sin B = (b/a)\\sin A = (7/5)(3/4) = 21/20 > 1$ ⇒ अमान्य.',
      },
    },
    {
      year: 2023,
      exam: 'JEE Main',
      tag: 'Heron',
      problem: {
        english: 'Sides 13, 14, 15. Find $\\Delta$ and $R$.',
        hindi: 'भुजाएँ 13, 14, 15. $\\Delta$ और $R$ खोजें।',
      },
      answer: {
        english: '$\\Delta = 84$, $R = 65/8$',
        hindi: '$\\Delta = 84$, $R = 65/8$',
      },
      solution: {
        english: '$s = 21$; $\\Delta = \\sqrt{21\\cdot8\\cdot7\\cdot6} = 84$. $R = abc/(4\\Delta) = 13\\cdot14\\cdot15 / 336 = 65/8$.',
        hindi: '$s = 21$; $\\Delta = \\sqrt{21\\cdot8\\cdot7\\cdot6} = 84$. $R = abc/(4\\Delta) = 13\\cdot14\\cdot15 / 336 = 65/8$.',
      },
    },
    {
      year: 2022,
      exam: 'JEE Main',
      tag: 'H&D',
      problem: {
        english: 'Tower casts shadow $30$ m when sun-altitude is $30°$. Height?',
        hindi: 'सूर्य की ऊंचाई $30°$ होने पर टावर की छाया $30$ मीटर पड़ती है। ऊंचाई?',
      },
      answer: {
        english: '$10\\sqrt 3$ m',
        hindi: '$10\\sqrt 3$ मी',
      },
      solution: {
        english: '$\\tan 30° = h/30 \\Rightarrow h = 30/\\sqrt 3 = 10\\sqrt 3$.',
        hindi: '$\\tan 30° = h/30 \\Rightarrow h = 30/\\sqrt 3 = 10\\sqrt 3$.',
      },
    },
    {
      year: 2021,
      exam: 'JEE Main',
      tag: 'Cosine',
      problem: {
        english: 'In $\\triangle ABC$ with $a^2 + b^2 = c^2$, find $C$.',
        hindi: '$\\triangle ABC$ में $a^2 + b^2 = c^2$ के साथ, $C$ खोजें।',
      },
      answer: {
        english: '$\\pi/2$',
        hindi: '$\\pi/2$',
      },
      solution: {
        english: '$\\cos C = 0$ from cosine rule.',
        hindi: 'कोसाइन नियम से $\\cos C = 0$।',
      },
    },
    {
      year: 2019,
      exam: 'JEE Main',
      tag: 'H&D',
      problem: {
        english: 'Two ships are observed from a $100$ m cliff at depression $30°, 45°$ on opposite sides. Distance between ships?',
        hindi: '$100$ मीटर की चट्टान से $30°, 45°$ विपरीत दिशा में दो जहाज देखे गए हैं। जहाजों के बीच की दूरी?',
      },
      answer: {
        english: '$100(1 + \\sqrt 3)$ m',
        hindi: '$100(1 + \\sqrt 3)$ मी',
      },
      solution: {
        english: 'Distances $= 100\\cot 30° + 100\\cot 45° = 100\\sqrt 3 + 100$.',
        hindi: 'दूरियाँ $= 100\\cot 30° + 100\\cot 45° = 100\\sqrt 3 + 100$.',
      },
    },
  ],
  next: {
    slug: 'mathematical-induction',
    title: 'Mathematical Induction',
  },
};

export default chapter;
